import { useState } from "react"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import { useSnackbar } from "notistack"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { BoundRadioGroup } from "lib/@components/binding/bound-components"
import { useRefresh } from "lib/@hooks/useRefresh"
import { Repeat } from "lib/@components/repeat"
import { scheduleCommentaryLabel } from "../lib/schedule-commentary-label"
import { moveScheduleCommentaryCategory } from "../controller/move-schedule-commentary-category"
import { ScheduleCommentaryMoveOption } from "./schedule-commentary-move-option"

export function ScheduleCommentaryMoveDialog({ open, onClose, item }) {
    const [target] = useState(() => ({ category: item.sectionId }))
    const [moving, setMoving] = useState(false)
    const refresh = useRefresh()
    const { enqueueSnackbar } = useSnackbar()

    const { commentarySections = [] } = useBoundContext()
    const options = commentarySections.filter((section) => section.id !== item.sectionId)

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Bound target={target} refresh={refresh}>
                <DialogTitle>Move "{scheduleCommentaryLabel(item)}"</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2">Choose which section this schedule should appear under.</Typography>
                        <BoundRadioGroup field="category" sideEffects={true}>
                            <Stack spacing={0.5}>
                                <Repeat
                                    list={options}
                                    item={<ScheduleCommentaryMoveOption />}
                                    keyFn={(section) => section.id}
                                />
                            </Stack>
                        </BoundRadioGroup>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={moving || target.category === item.sectionId}
                        onClick={handleMove}
                        data-cy="schedule-commentary-move-dialog-confirm"
                    >
                        Move
                    </Button>
                </DialogActions>
            </Bound>
        </Dialog>
    )

    async function handleMove() {
        setMoving(true)
        try {
            await moveScheduleCommentaryCategory({ id: item.id, category: target.category })
            const section = options.find((option) => option.id === target.category)
            enqueueSnackbar(`Moved to ${section?.title ?? "section"}`, { variant: "success" })
            onClose()
        } catch (error) {
            enqueueSnackbar(error.message || "Failed to move schedule", { variant: "error" })
        } finally {
            setMoving(false)
        }
    }
}
